import { Button } from "@/components/shadcn/button";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { copyToClipboard, getFileImage } from "@/utils/generic";
import ImageWithFallback from "@/components/ImageWithFallback";
import { SKIP, visit } from "unist-util-visit";
import type { Plugin } from "unified";
import type { Node } from "unist";
import type { Break, Root, Text } from "mdast";
import { memo, useMemo } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";

import { Copy } from "lucide-react";

import { cn } from "@/lib/utils";

interface IMarkdownOptions {
  hideCodeTitle?: boolean;
  markdownClass?: string;
  bgColor?: string;
  // Render a single newline as a hard line break (GitHub-comment style),
  // instead of collapsing it to a space per strict CommonMark. Off by default
  // so LLM/chat markdown keeps standard paragraph behaviour.
  breaks?: boolean;
  // Inline-only rendering for compact previews: keeps emphasis, code spans,
  // links and lists; block containers (headings, blockquotes, code fences)
  // unwrap to their text at body size, and tables collapse to a placeholder.
  inline?: boolean;
}

const INLINE_ALLOWED_ELEMENTS = [
  "p",
  "br",
  "em",
  "strong",
  "del",
  "a",
  "code",
  "ul",
  "ol",
  "li",
  "table",
];

// `table` stays allowed only so this override can intercept it — unwrapping
// a table concatenates its cell text into an unreadable run.
const INLINE_COMPONENTS: Components = {
  code({ node, className, children, ...props }) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  a({ node, children, ...props }) {
    return (
      <a
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </a>
    );
  },
  table() {
    return <span className="italic text-muted-foreground">(table)</span>;
  },
};

// Mirrors `remark-breaks`: split each text node on "\n" and insert hard-break
// nodes. Kept local to avoid a dependency for ~10 lines.
const remarkLineBreaks: Plugin<[], Root> = () => (tree: Root) => {
  visit(tree, "text", (node: Text, index, parent) => {
    if (parent === undefined || index === undefined) return;
    if (!node.value.includes("\n")) return;
    const segments = node.value.split("\n");
    const replacement: Array<Text | Break> = [];
    segments.forEach((segment, i) => {
      if (i > 0) replacement.push({ type: "break" });
      replacement.push({ type: "text", value: segment });
    });
    parent.children.splice(index, 1, ...replacement);
    return [SKIP, index + replacement.length];
  });
};

const SpotMarkdown = memo(
  ({ markdown, options }: { markdown: string; options?: IMarkdownOptions }) => {
    // No remark-directive here on purpose: SpotMarkdown renders run outputs,
    // chat and other display text, none of which use directive syntax. It has
    // no directive component handler, so enabling remark-directive only ever
    // *ate* text — it parses any `:word` (e.g. "echo:asd") as a directive node
    // and drops it. `:var[...]` variable refs are the only real directive use,
    // and they live in the V2 editor's Milkdown pipeline (variablePlugin.tsx),
    // resolved to values long before anything reaches this renderer.
    const remarkPlugins = useMemo(
      () => (options?.breaks ? [remarkGfm, remarkLineBreaks] : [remarkGfm]),
      [options?.breaks]
    );

    return (
      <ErrorBoundary
        fallback={
          <NoxusAlert
            look="banner"
            variant="destructive"
            title="An error occurred while rendering this value"
          />
        }
      >
        <div
          className={cn(
            "markdown-body",
            options?.inline && "markdown-inline",
            options?.markdownClass
          )}
          style={{
            backgroundColor: options?.bgColor,
          }}
        >
          <Markdown
            remarkPlugins={remarkPlugins}
            className="markdown"
            allowedElements={
              options?.inline ? INLINE_ALLOWED_ELEMENTS : undefined
            }
            unwrapDisallowed={options?.inline}
            // rehypePlugins={[rehypeHighlight]}
            components={
              options?.inline
                ? INLINE_COMPONENTS
                : {
                    code({ node, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");

                      if (
                        node?.position?.start.line === node?.position?.end.line
                      ) {
                        return (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }

                      return (
                        <div>
                          {!options?.hideCodeTitle && (
                            <div
                              className="flex justify-between items-center pl-4 pr-2 py-2 border border-border rounded-t-lg rounded-b-none -mx-3 -mt-3 bg-white"
                              id="white-bg"
                            >
                              <p className="text-sm !mb-0">
                                {match ? match[1] : ""}
                              </p>

                              <Button
                                aria-label="Copy"
                                variant="ghost"
                                size="iconsm"
                                onClick={() =>
                                  copyToClipboard(
                                    String(children).replace(/\n$/, "")
                                  )
                                }
                              >
                                <Copy className="size-4" strokeWidth={1.25} />
                              </Button>
                            </div>
                          )}
                          {match ? (
                            <SyntaxHighlighter
                              language={match[1]}
                              style={oneLight}
                              customStyle={{
                                fontSize: "0.8125rem",
                                lineHeight: 1.5,
                                margin: 0,
                              }}
                              codeTagProps={{
                                style: {
                                  fontSize: "0.8125rem",
                                  lineHeight: 1.5,
                                },
                              }}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          )}
                        </div>
                      );
                    },
                    a({ node, children, ...props }) {
                      return (
                        <a target="_blank" rel="noreferrer noopener" {...props}>
                          {children}
                        </a>
                      );
                    },
                    img: ({ node, children, ...props }) => {
                      return (
                        <div className="relative w-full aspect-square">
                          <ImageWithFallback
                            alt={props.alt || ""}
                            src={(props.src as string | undefined) || ""}
                            fill
                            style={{ objectFit: "contain" }}
                          />
                        </div>
                      );
                    },
                    div: ({ node, ...props }) => {
                      return <div {...props} />;
                    },
                  }
            }
          >
            {markdown}
          </Markdown>
        </div>
      </ErrorBoundary>
    );
  }
);

// Add display name to fix linter error
SpotMarkdown.displayName = "SpotMarkdown";

export { SpotMarkdown };
